import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { useAuth } from "../context/AuthContext";
import { usePos } from "../context/PosContext";
import { PageHeader, Loader, Empty } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import PermissionEditor from "../components/PermissionEditor";
import { Plus, Trash2, Pencil, ShieldCheck, User, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const clone = (o) => JSON.parse(JSON.stringify(o || {}));
const EMPTY = { name: "", email: "", password: "", role: "employe", pos_id: "", profile: "employe" };

export default function UsersPage() {
  const { user } = useAuth();
  const { posList } = usePos();
  const { data, loading, refetch } = useData("/users");
  const [profiles, setProfiles] = useState([]);
  const [schema, setSchema] = useState(null);

  const loadProfiles = () => api.get("/profiles").then(({ data }) => setProfiles(data)).catch(() => {});
  useEffect(() => {
    loadProfiles();
    api.get("/permissions/schema").then(({ data }) => setSchema(data)).catch(() => {});
  }, []);

  const profileByKey = (k) => profiles.find((p) => p.key === k);
  const profileName = (k) => profileByKey(k)?.nom || k || "—";

  // ---- User modal ----
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [custom, setCustom] = useState(false);
  const [permVal, setPermVal] = useState({ permissions: {}, specials: {} });

  const baseMatrixForProfile = (key) => {
    const p = profileByKey(key);
    return { permissions: clone(p?.permissions || {}), specials: clone(p?.specials || {}) };
  };

  const openNew = () => {
    setEditId(null);
    setForm({ ...EMPTY, pos_id: posList[0]?.id || "" });
    setCustom(false);
    setPermVal(baseMatrixForProfile("employe"));
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditId(u.id);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, pos_id: u.pos_id || "", profile: u.profile || (u.role === "admin" ? "administrateur" : "employe") });
    const hasCustom = u.permissions && Object.keys(u.permissions).length > 0;
    setCustom(hasCustom);
    const base = baseMatrixForProfile(u.profile || "employe");
    if (hasCustom) {
      setPermVal({ permissions: { ...base.permissions, ...clone(u.permissions) }, specials: { ...base.specials, ...clone(u.specials) } });
    } else {
      setPermVal(base);
    }
    setOpen(true);
  };

  const onProfileChange = (key) => {
    setForm({ ...form, profile: key });
    if (!custom) setPermVal(baseMatrixForProfile(key));
  };
  const onCustomToggle = (checked) => {
    setCustom(checked);
    if (checked) setPermVal(baseMatrixForProfile(form.profile));
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.role === "employe" && !form.pos_id) return toast.error("Choisissez un point de vente pour l'employé");
    const payload = {
      name: form.name, email: form.email, role: form.role,
      pos_id: form.role === "admin" ? null : form.pos_id,
      profile: form.profile,
      permissions: custom ? permVal.permissions : {},
      specials: custom ? permVal.specials : {},
    };
    try {
      if (editId) {
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editId}`, payload);
        toast.success("Utilisateur mis à jour");
      } else {
        payload.password = form.password;
        await api.post("/auth/register", payload);
        toast.success("Utilisateur créé");
      }
      setOpen(false); refetch();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer ${u.name} ?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Supprimé"); refetch(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // ---- Profiles modal ----
  const [profOpen, setProfOpen] = useState(false);
  const [selProf, setSelProf] = useState(null); // profile doc being edited
  const [profName, setProfName] = useState("");
  const [profVal, setProfVal] = useState({ permissions: {}, specials: {} });
  const [creatingProf, setCreatingProf] = useState(false);

  const openProfiles = () => { setSelProf(null); setProfOpen(true); };
  const selectProfile = (p) => {
    setSelProf(p); setCreatingProf(false); setProfName(p.nom);
    setProfVal({ permissions: clone(p.permissions), specials: clone(p.specials) });
  };
  const newProfile = () => {
    setSelProf(null); setCreatingProf(true); setProfName("");
    const empty = {}; (schema?.sections || []).forEach((s) => { empty[s.key] = {}; (schema.actions || []).forEach((a) => { empty[s.key][a] = false; }); });
    const espec = {}; (schema?.specials || []).forEach((sp) => { espec[sp.key] = false; });
    setProfVal({ permissions: empty, specials: espec });
  };
  const saveProfile = async () => {
    if (!profName.trim()) return toast.error("Nom du profil requis");
    try {
      if (creatingProf) {
        await api.post("/profiles", { nom: profName, permissions: profVal.permissions, specials: profVal.specials });
        toast.success("Profil créé");
      } else if (selProf) {
        await api.put(`/profiles/${selProf.id}`, { nom: profName, permissions: profVal.permissions, specials: profVal.specials });
        toast.success("Profil mis à jour");
      }
      setCreatingProf(false); setSelProf(null); loadProfiles();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteProfile = async (p) => {
    if (!window.confirm(`Supprimer le profil ${p.nom} ?`)) return;
    try { await api.delete(`/profiles/${p.id}`); toast.success("Profil supprimé"); setSelProf(null); loadProfiles(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const fmt = (s) => new Date(s).toLocaleDateString("fr-FR");

  return (
    <div>
      <PageHeader title="Utilisateurs & Permissions" subtitle="Comptes, profils et permissions granulaires par rubrique et par action.">
        <button className="tm-btn-ghost tm-btn" onClick={openProfiles} data-testid="manage-profiles-btn"><SlidersHorizontal size={16} /> Profils de permissions</button>
        <button className="tm-btn" onClick={openNew} data-testid="add-user-btn"><Plus size={16} /> Nouvel utilisateur</button>
      </PageHeader>

      {loading ? <Loader /> : data.length === 0 ? <Empty text="Aucun utilisateur." /> : (
        <div className="tm-card overflow-x-auto" data-testid="users-table">
          <table className="tm-table">
            <thead>
              <tr><th>Nom</th><th>Email</th><th>Rôle</th><th>Profil</th><th>Point de vente</th><th>Créé le</th><th></th></tr>
            </thead>
            <tbody>
              {data.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="font-semibold flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-black text-xs" style={{ background: "var(--tm-gold)" }}>{u.name.charAt(0).toUpperCase()}</span>
                    {u.name}
                  </td>
                  <td className="text-[var(--tm-muted)]">{u.email}</td>
                  <td>
                    {u.role === "admin" ? (
                      <span className="tm-badge" style={{ background: "rgba(212,175,55,.15)", color: "var(--tm-gold)" }}><ShieldCheck size={13} /> Administrateur</span>
                    ) : (
                      <span className="tm-badge" style={{ background: "var(--tm-surface-2)", color: "var(--tm-muted)" }}><User size={13} /> Employé</span>
                    )}
                  </td>
                  <td>
                    <span className="tm-badge" style={{ background: "var(--tm-surface-2)", color: "var(--tm-beige)" }}>{profileName(u.profile)}</span>
                    {u.permissions && Object.keys(u.permissions).length > 0 && <span className="tm-badge ml-1" style={{ background: "rgba(212,175,55,.15)", color: "var(--tm-gold)" }}>personnalisé</span>}
                  </td>
                  <td className="text-[var(--tm-muted)]">{u.role === "admin" ? "Tous" : (u.pos_nom || "—")}</td>
                  <td className="text-[var(--tm-muted)]">{fmt(u.created_at)}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => openEdit(u)} className="text-[var(--tm-muted)] hover:text-[var(--tm-gold)] mr-3" data-testid={`edit-user-${u.id}`}><Pencil size={16} /></button>
                    {u.id !== user.id && <button onClick={() => remove(u)} className="text-[var(--tm-muted)] hover:text-[#F44336]" data-testid={`delete-user-${u.id}`}><Trash2 size={16} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* User modal */}
      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifier l'utilisateur" : "Nouvel utilisateur"} testid="user-modal" wide>
        <form onSubmit={save}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nom complet"><input className="tm-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></Field>
            <Field label="Email"><input type="email" className="tm-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={editId ? "Nouveau mot de passe (laisser vide pour conserver)" : "Mot de passe"}>
              <input type="password" className="tm-input" required={!editId} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
            </Field>
            <Field label="Rôle">
              <select className="tm-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
                <option value="employe">Employé</option><option value="admin">Administrateur</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {form.role === "employe" && (
              <Field label="Point de vente">
                <select className="tm-input" value={form.pos_id} onChange={(e) => setForm({ ...form, pos_id: e.target.value })} data-testid="user-pos">
                  <option value="">— Choisir —</option>
                  {posList.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
                </select>
              </Field>
            )}
            <Field label="Profil de permissions">
              <select className="tm-input" value={form.profile} onChange={(e) => onProfileChange(e.target.value)} data-testid="user-profile" disabled={form.role === "admin"}>
                {profiles.map((p) => <option key={p.key} value={p.key}>{p.nom}</option>)}
              </select>
            </Field>
          </div>

          {form.role === "admin" ? (
            <p className="text-sm text-[var(--tm-muted)] my-3">L'administrateur dispose d'un accès total à toutes les rubriques et actions.</p>
          ) : (
            <>
              <label className="flex items-center gap-2 cursor-pointer my-3 text-sm">
                <input type="checkbox" checked={custom} onChange={(e) => onCustomToggle(e.target.checked)} data-testid="user-custom-perms" />
                <span>Personnaliser les permissions de cet utilisateur (indépendamment du profil)</span>
              </label>
              <PermissionEditor schema={schema} value={permVal} onChange={setPermVal} disabled={!custom} />
            </>
          )}

          <button type="submit" className="tm-btn w-full mt-4" data-testid="user-save">{editId ? "Enregistrer" : "Créer le compte"}</button>
        </form>
      </Modal>

      {/* Profiles modal */}
      <Modal open={profOpen} onClose={() => { setProfOpen(false); setSelProf(null); setCreatingProf(false); }} title="Profils de permissions" testid="profiles-modal" wide>
        <div className="flex flex-wrap gap-2 mb-4">
          {profiles.map((p) => (
            <button key={p.id} type="button" onClick={() => selectProfile(p)} data-testid={`profile-chip-${p.key}`}
              className="tm-badge" style={selProf?.id === p.id ? { background: "var(--tm-gold)", color: "#000" } : { background: "var(--tm-surface-2)", color: "var(--tm-beige)" }}>
              {p.nom}{p.systeme ? "" : " ✎"}
            </button>
          ))}
          <button type="button" onClick={newProfile} className="tm-badge" style={{ background: "rgba(212,175,55,.15)", color: "var(--tm-gold)" }} data-testid="new-profile-btn"><Plus size={12} /> Nouveau profil</button>
        </div>

        {(selProf || creatingProf) ? (
          <div>
            <Field label="Nom du profil"><input className="tm-input" value={profName} onChange={(e) => setProfName(e.target.value)} data-testid="profile-name" /></Field>
            <PermissionEditor schema={schema} value={profVal} onChange={setProfVal} />
            <div className="flex gap-2 mt-4">
              <button type="button" className="tm-btn flex-1" onClick={saveProfile} data-testid="profile-save">Enregistrer le profil</button>
              {selProf && !selProf.systeme && <button type="button" className="tm-btn-danger tm-btn" onClick={() => deleteProfile(selProf)}><Trash2 size={16} /></button>}
            </div>
          </div>
        ) : (
          <p className="text-sm text-[var(--tm-muted)]">Sélectionnez un profil à personnaliser ou créez-en un nouveau. Les profils système peuvent être modifiés mais pas supprimés.</p>
        )}
      </Modal>
    </div>
  );
}
